/**
 * Global error handler utilities
 */

declare global {
  interface Window {
    __TM_ERROR_HANDLERS_INSTALLED__?: boolean;
    __TM_ORIGINAL_CONSOLE_ERROR__?: typeof console.error;
    __TM_IN_CONSOLE_ERROR__?: boolean;
  }
}

import {
  installFetchDiagnostics,
  reportClientDiagnostic,
  reportUnhandledError,
} from "@/utils/client-diagnostics";
import {
  installDiagnosticsBuffer,
  recordConsole,
} from "@/utils/diagnostics-buffer";

// List of known extension-related errors to ignore
const IGNORED_ERROR_PATTERNS = [
  "Could not establish connection. Receiving end does not exist",
  "Extension context invalidated",
  "The message port closed before a response was received",
  "Cannot access a chrome:// URL",
  "chrome-extension://",
  // Prevent noisy React overlay cascades
  "Maximum update depth exceeded",
  "Too many re-renders",
];

/**
 * Check if an error is from a browser extension and should be ignored
 */
export function isExtensionError(error: Error | string): boolean {
  const errorMessage = typeof error === "string" ? error : error.message;

  return IGNORED_ERROR_PATTERNS.some((pattern) =>
    errorMessage.includes(pattern),
  );
}

/**
 * Install global error handlers to filter out extension errors
 */
export function installErrorHandlers(): void {
  if (typeof window === "undefined") return;

  // Avoid multiple installations (e.g., HMR, multiple mounts)
  if (window.__TM_ERROR_HANDLERS_INSTALLED__) return;
  installFetchDiagnostics();
  installDiagnosticsBuffer();

  // Persist the original console.error the first time we install
  const originalConsoleError =
    window.__TM_ORIGINAL_CONSOLE_ERROR__ || console.error.bind(console);
  window.__TM_ORIGINAL_CONSOLE_ERROR__ = originalConsoleError;

  // Override console.error to filter extension errors (idempotent)
  let lastMsg = "";
  let lastTime = 0;
  let repeatCount = 0;
  console.error = (...args: unknown[]) => {
    // Re-entrancy guard: avoid nested console.error loops
    if (window.__TM_IN_CONSOLE_ERROR__) {
      try {
        originalConsoleError(
          ...(args as Parameters<typeof originalConsoleError>),
        );
      } catch {
        // Swallow to avoid secondary failures inside logging
      }
      return;
    }
    window.__TM_IN_CONSOLE_ERROR__ = true;

    // Check if any argument contains extension error patterns
    const hasExtensionError = args.some((arg) => {
      if (typeof arg === "string") return isExtensionError(arg);
      if (arg instanceof Error) return isExtensionError(arg);
      return false;
    });

    // Basic duplicate suppression: same message burst within 1s
    const firstArg = args[0];
    const msg = (() => {
      if (firstArg === undefined) return "";
      try {
        if (firstArg instanceof Error) {
          return firstArg.message;
        }
        if (typeof firstArg === "string") {
          return firstArg;
        }
        return JSON.stringify(firstArg);
      } catch {
        return String(firstArg);
      }
    })();
    const now = Date.now();
    if (msg === lastMsg && now - lastTime < 1000) {
      repeatCount++;
      if (repeatCount > 3) {
        // Drop excessive repeats in a tight loop
        window.__TM_IN_CONSOLE_ERROR__ = false;
        return;
      }
    } else {
      lastMsg = msg;
      lastTime = now;
      repeatCount = 0;
    }

    if (!hasExtensionError) {
      try {
        recordConsole("error", args);
      } catch {
        // never let logging break
      }
      reportClientDiagnostic({
        eventType: "console_error",
        severity: "error",
        message: msg || "console.error",
        details: {
          args: args.map((arg) => {
            if (arg instanceof Error) {
              return { name: arg.name, message: arg.message, stack: arg.stack };
            }
            if (typeof arg === "string") return arg;
            try {
              return JSON.parse(JSON.stringify(arg));
            } catch {
              return String(arg);
            }
          }).slice(0, 5),
        },
      });
      try {
        // Always call the captured original, not the possibly re-wrapped version
        originalConsoleError(...(args as Parameters<typeof originalConsoleError>));
      } finally {
        window.__TM_IN_CONSOLE_ERROR__ = false;
      }
      return;
    }

    window.__TM_IN_CONSOLE_ERROR__ = false;
  };

  // Handle unhandled promise rejections
  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    const { reason } = event;
    if (typeof reason === "string" || reason instanceof Error) {
      if (isExtensionError(reason)) {
        event.preventDefault(); // Prevent logging
        return;
      }
    }
    reportUnhandledError(reason, "unhandled_rejection");
  };
  window.addEventListener("unhandledrejection", onUnhandledRejection, {
    capture: true,
  });

  // Handle global errors
  const onWindowError = (event: ErrorEvent) => {
    if (event.message && isExtensionError(event.message)) {
      event.preventDefault(); // Prevent logging
      return;
    }
    reportUnhandledError(event.error || event.message, "window_error", {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  };
  window.addEventListener("error", onWindowError, { capture: true });

  window.__TM_ERROR_HANDLERS_INSTALLED__ = true;
}
